import React, { useContext } from 'react';

import { Badge, Button, Col, Empty, Tooltip, Typography } from 'antd';

import StatCard from '@components/StatCard';

import {
  CalendarOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  FileTextOutlined,
  PieChartOutlined,
} from '@ant-design/icons';
import { BiBlock } from 'react-icons/bi';
import { useTranslation } from 'react-i18next';
import { getLocaleString } from '@utils/locales';
import { bytesFormat, extractDateInISOFormat } from '@utils/formatters';
import RoleChecker from '@components/RoleChecker';
import rolesConstants from '@utils/rolesConstants';
import { ContainerRow } from '@pages/Assets/Inventory/InventoryPreview/TabPanesDetails/styles';
import useSwr from '@hooks/useSwr';
import { FiExternalLink } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import SessionContext from '@providers/Session/SessionContext';
import { FaNetworkWired } from 'react-icons/fa';
import CustomCard from '@components/CustomCard';
import { ApplicationsConstants } from '@utils/applicationsConstants';

const { Paragraph } = Typography;

const TabPaneInventory = ({ iccid }: { iccid: string }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation(['inventory-detailed', 'inventory']);
  const { t: tStatus } = useTranslation('status');
  const navigate = useNavigate();
  const { isRoot, isMotPay } = useContext(SessionContext);

  const { data: inventoryData } = useSwr(`/service-proxy/inventory/MOT`, {
    method: 'post',
    data: {
      filters: {
        iccid: [iccid],
        showContracted: !isRoot,
      },
      pagination: {
        page: 0,
        linesPerPage: 20,
      },
    },
  });

  const deviceProps =
    inventoryData?.content && inventoryData?.content?.length > 0
      ? inventoryData.content[0]
      : undefined;

  return (
    <>
      {!deviceProps ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <ContainerRow gutter={[12, 12]}>
          <Col lg={24} xs={24}>
            <div style={{ display: 'flex', justifyContent: 'end' }}>
              <Tooltip title={t('viewOnPage')}>
                <Button
                  icon={
                    <FiExternalLink style={{ marginTop: 4, marginLeft: 2 }} />
                  }
                  onClick={() => {
                    navigate(
                      `/${ApplicationsConstants.MOT_PAY}/assets/inventory?msisdn=${deviceProps?.msisdn}`,
                    );
                  }}
                />
              </Tooltip>
            </div>
          </Col>

          <RoleChecker client="inventory" role={rolesConstants.INVENT_STATUS}>
            <Col lg={12} xs={24}>
              <StatCard
                title={t('status')}
                value={`${tStatus(deviceProps?.status || '')}` || '-'}
                icon={
                  <Badge
                    status={
                      deviceProps?.status
                        ? tStatus(`badgeStatus.${deviceProps?.status}`)
                        : 'error'
                    }
                  />
                }
                loading={!deviceProps}
              />
            </Col>
          </RoleChecker>
          <RoleChecker client="inventory" role={rolesConstants.INVENT_OPERATOR}>
            <>
              <Col lg={12} xs={24}>
                <StatCard
                  title={t('operator')}
                  icon={<PhoneOutlined />}
                  value={
                    deviceProps?.operator || deviceProps?.subOperator || '-'
                  }
                  loading={!deviceProps}
                />
              </Col>
              <Col lg={12} xs={24}>
                <StatCard
                  title={t('connected-operator')}
                  icon={<PhoneOutlined />}
                  value={deviceProps?.connectedOperator || '-'}
                  loading={!deviceProps}
                />
              </Col>
            </>
          </RoleChecker>

          <RoleChecker client="inventory" role={rolesConstants.INVENT_ONLINE}>
            <Col lg={12} xs={24}>
              <StatCard
                title={t('connection-status')}
                icon={
                  <Badge
                    status={tStatus(
                      deviceProps?.online
                        ? 'badgeStatus.ONLINE'
                        : 'badgeStatus.OFFLINE',
                    )}
                  />
                }
                value={
                  deviceProps?.online
                    ? t<string>('ONLINE') ?? ''
                    : t<string>('OFFLINE')
                }
                loading={!deviceProps}
              />
            </Col>
          </RoleChecker>
          <RoleChecker
            client="inventory"
            role={rolesConstants.INVENT_LAST_CONNECTION}
          >
            <Col lg={12} xs={24}>
              <StatCard
                title={t('last-connection')}
                value={
                  deviceProps && deviceProps.lastConnection
                    ? new Date(deviceProps.lastConnection).toLocaleString(
                        getLocaleString(language),
                      )
                    : '-'
                }
                icon={<CalendarOutlined />}
                loading={!deviceProps}
              />
            </Col>
          </RoleChecker>
          <RoleChecker
            client="inventory"
            role={rolesConstants.INVENT_ACTIVATION_DATE}
          >
            <Col lg={12} xs={24}>
              <StatCard
                title={t('activation-date')}
                value={
                  deviceProps && deviceProps.activationDate
                    ? extractDateInISOFormat(deviceProps.activationDate)
                    : '-'
                }
                icon={<CalendarOutlined />}
                loading={!deviceProps}
              />
            </Col>
          </RoleChecker>

          <RoleChecker
            client="inventory"
            role={rolesConstants.INVENT_NETWORK_BLOCK_STATUS}
          >
            <Col lg={12} xs={24}>
              <StatCard
                title={t('blocking-status')}
                value={
                  `${t(
                    `inventory:lock-status.types.${
                      deviceProps?.networkBlockStatus ?? 'NONE'
                    }`,
                  )}` || '-'
                }
                icon={
                  <BiBlock
                    style={{
                      fontSize: '1.1rem',
                    }}
                  />
                }
                loading={!deviceProps}
              />
            </Col>
          </RoleChecker>
          {deviceProps?.technology && (
            <Col lg={12} xs={24}>
              <StatCard
                title={t('inventory:table.technology')}
                value={deviceProps?.technology}
                icon={
                  <FaNetworkWired
                    style={{
                      fontSize: '1.1rem',
                    }}
                  />
                }
                loading={!deviceProps}
              />
            </Col>
          )}
          {deviceProps?.deviceNumber && isMotPay && (
            <Col lg={12} xs={24}>
              <StatCard
                title={t('inventory:table.deviceNumber')}
                value={deviceProps?.deviceNumber}
                icon={
                  <PhoneOutlined
                    style={{
                      fontSize: '1.1rem',
                    }}
                  />
                }
                loading={!deviceProps}
              />
            </Col>
          )}
          {deviceProps?.ggsn && isMotPay && (
            <Col lg={12} xs={24}>
              <StatCard
                title="GGSN"
                value={deviceProps?.ggsn}
                icon={
                  <PhoneOutlined
                    style={{
                      fontSize: '1.1rem',
                    }}
                  />
                }
                loading={!deviceProps}
              />
            </Col>
          )}
          <RoleChecker client="inventory" role={rolesConstants.INVENT_CITY}>
            <Col span={24}>
              <StatCard
                title={t('city')}
                value={(() => {
                  if (deviceProps) {
                    if (deviceProps.city && deviceProps.state) {
                      return `${deviceProps.city}, ${deviceProps.state}`;
                    }
                    if (deviceProps.city) {
                      return deviceProps.city;
                    }
                    if (deviceProps.state) {
                      return deviceProps.state;
                    }
                    return '-';
                  }
                  return '-';
                })()}
                icon={<EnvironmentOutlined />}
                loading={!deviceProps}
              />
            </Col>
          </RoleChecker>
          <RoleChecker
            client="inventory"
            role={rolesConstants.INVENT_ACCOUNT_DES}
          >
            <Col span={24}>
              <StatCard
                title={t('plan')}
                icon={<FileTextOutlined />}
                value={deviceProps?.accountDesc || '-'}
                loading={!deviceProps}
              />
            </Col>
          </RoleChecker>

          <RoleChecker
            client="inventory"
            role={rolesConstants.INVENT_CONSUMPTION}
          >
            <Col span={24}>
              <StatCard
                title={t('consumption')}
                icon={<PieChartOutlined />}
                value={bytesFormat(deviceProps?.consumptionTotal ?? '0')}
                suffix={
                  deviceProps?.franchise
                    ? `/ ${bytesFormat(deviceProps?.franchise)}`
                    : ''
                }
                loading={!deviceProps}
              />
            </Col>
          </RoleChecker>

          <RoleChecker client="inventory" role={rolesConstants.INVENT_IMEI}>
            <Col lg={12} xs={24}>
              <StatCard
                title="IMEI"
                value={deviceProps?.imei || '-'}
                loading={!deviceProps}
              />
            </Col>
          </RoleChecker>

          <RoleChecker client="inventory" role={rolesConstants.INVENT_MSISDN}>
            <Col lg={12} xs={24}>
              <StatCard
                title="MSISDN"
                value={deviceProps?.msisdn || '-'}
                loading={!deviceProps}
              />
            </Col>
          </RoleChecker>
          <RoleChecker
            client="inventory"
            role={rolesConstants.INVENT_OBSERVATION}
          >
            <Col span={24}>
              <CustomCard loading={!deviceProps} title={t('note')}>
                <Paragraph>{deviceProps?.observation}</Paragraph>
              </CustomCard>
            </Col>
          </RoleChecker>
        </ContainerRow>
      )}
    </>
  );
};

export default TabPaneInventory;
